import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography
} from '@material-ui/core';
import { Link as GatsbyLink } from 'gatsby';
import { ChevronDown } from 'lucide-react';
import React from 'react';
import { Helmet } from 'react-helmet';
import styled from '../../styled';

const FaqContainer = styled('div')`
  max-width: 760px;
  margin: 0 auto;
`;

const Question = styled('h3')`
  font-size: 16px;
  font-weight: 600;
  color: #0e1c2b;
  margin: ${(p) => p.theme.spacing(1)}px 0;
`;

const Link = styled(GatsbyLink)`
  color: ${(p) => p.theme.palette.primary.main};
  transition: 0.1s border-bottom linear;
  border-bottom: 1px solid ${(p) => p.theme.palette.primary.main};

  &:hover {
    border-bottom: 3px solid ${(p) => p.theme.palette.primary.main};
  }
`;

// answerText is the plain-text version used for FAQPage structured data.
// answer (optional) is the rich version rendered on the page.
const faqs: {
  question: string;
  answerText: string;
  answer?: React.ReactNode;
}[] = [
  {
    question: 'How much does Affilimate cost?',
    answerText:
      "Pricing is based on a combination of the overall volume running through the platform and which features and products you need. We tailor plans for companies starting their commerce journey through to companies with sophisticated internal tools requiring custom integration from our team. We'll send you a list of KPIs for pricing after our first call."
  },
  {
    question: 'Will Affilimate work for our website or CMS?',
    answerText:
      'Most likely, yes! Affilimate works with all major website builders and CMS solutions, and can be integrated with custom sites as well.'
  },
  {
    question: 'How long does it take to set up Affilimate?',
    answerText:
      "Even publishers with custom tech stacks are typically able to go from kickoff to live with a trial in under 2 weeks. For publishers with a standard setup, account configuration and your first insights takes less than 24 hours and often doesn't require developer resources."
  },
  {
    question: 'Does Affilimate have an API?',
    answerText:
      "Yes, Affilimate has a variety of publisher API endpoints for tasks like pulling commission data, sending custom events, searching your advertisers, and generating links. Reach out to us with your use case and we'll be able to share more about our API's capabilities."
  },
  {
    question: 'Does Affilimate integrate with Google Analytics or AEM?',
    answerText:
      "Affilimate tracks all its own event data, including pageviews, clicks, and impressions. For this reason, it's not necessary to rely on this data from Google Analytics, Adobe Experience Manager, or other third-party web analytics tools. This out-of-the-box approach saves you engineering time and resources, allows Affilimate to offer more advanced and custom features, and often results in more complete data."
  },
  {
    question: 'Which networks and platforms does Affilimate integrate with?',
    answerText:
      'Affilimate integrates with 100+ affiliate platforms, and thousands of programs through our integrations. You can search a list of the affiliate platforms we support on our Integrations page.',
    answer: (
      <>
        Affilimate integrates with 100+ affiliate platforms, and thousands of
        programs through our integrations.
        <br />
        <br />
        You can search a list of the affiliate platforms we support on our{' '}
        <Link to="/integrations/">Integrations page</Link>.
      </>
    )
  },
  {
    question: 'How can I speak with someone from your team?',
    answerText:
      "Get in touch via our contact form! We're excited to talk about affiliate marketing and analytics with you, and see if our platform is a fit.",
    answer: (
      <>
        Get in touch via <Link to="/contact/">our contact form</Link>! We're
        excited to talk about affiliate marketing and analytics with you, and
        see if our platform is a fit.
      </>
    )
  },
  {
    question: "Where can we learn more about Affilimate's features?",
    answerText:
      "You can learn more about Affilimate's core features on our features hub, or book a demo with our team to see how Affilimate can help your business.",
    answer: (
      <>
        You can learn more about Affilimate's core features on our{' '}
        <Link to="/product/">features hub</Link>, or{' '}
        <Link to="/demo/">book a demo</Link> with our team to see how Affilimate
        can help your business.
      </>
    )
  }
];

const faqStructuredData = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answerText
    }
  }))
});

export const Faq = () => (
  <FaqContainer>
    <Helmet>
      <script type="application/ld+json">{faqStructuredData}</script>
    </Helmet>
    {faqs.map((faq, index) => (
      <Accordion key={faq.question} defaultExpanded={index === 0}>
        <AccordionSummary
          expandIcon={<ChevronDown size={20} />}
          aria-controls={`faq-${index}`}
          id={`faq-${index}`}
        >
          <Question>{faq.question}</Question>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" component="p" style={{ color: '#444' }}>
            {faq.answer ?? faq.answerText}
          </Typography>
        </AccordionDetails>
      </Accordion>
    ))}
  </FaqContainer>
);
